import { CLIENT_BUILD } from './build-info.js';
import { captureBrowserObservation, installBrowserErrorTracking, reportBrowserError, updateBrowserErrorContext } from './error-tracking.js';
import { ACTIONS, type Observe } from '../shared/observability.js';
import { pageSessionId } from './observation-context.js';
import { normalizeEvent, TelemetryOutbox, type StoredClientEvent } from './telemetry-outbox.js';

export interface ClientEvent {
  type: string;
  at: string;
  message?: string;
  planId?: string;
  tripId?: string;
  detail?: Record<string, unknown>;
}

const ENDPOINT = '/api/client-events';
const MAX_BATCH = 50;
/** Statuses that mean this event will never be accepted, not that the API is down. */
const PERMANENT_REFUSALS = new Set([400, 409, 413, 422]);
const uuid = () => typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
const sessionId = pageSessionId;
const outbox = new TelemetryOutbox();
let timer: ReturnType<typeof setTimeout> | undefined;
let running = false;
let hotGeneration = 'page-start';

export function flushEvents() {
  if (timer) clearTimeout(timer);
  timer = undefined;
  if (running || typeof window === 'undefined') return;
  running = true;
  void (async () => {
    try {
      const pending = await outbox.pending();
      const refused: string[] = [];
      let limit = MAX_BATCH;
      while (pending.length) {
        const batch: StoredClientEvent[] = [];
        let bytes = 0;
        while (pending.length && batch.length < limit) {
          const event = pending[0]!;
          const length = new TextEncoder().encode(JSON.stringify(event)).length + 2;
          if (batch.length && bytes + length > 48 * 1024) break;
          batch.push(pending.shift()!); bytes += length;
        }
        const response = await fetch(ENDPOINT, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ sessionId, events: batch }), keepalive: true });
        if (response.ok) { await outbox.acknowledge(batch.map((event) => event.id)); continue; }
        // The API is unreachable or unwell: keep everything and retry later.
        if (!PERMANENT_REFUSALS.has(response.status)) break;
        // One bad event must not stall the rest: find it by sending singly, then drop only it.
        if (batch.length > 1) { pending.unshift(...batch); limit = 1; continue; }
        await outbox.reject([batch[0]!.id]);
        refused.push(`${batch[0]!.type} ${batch[0]!.id} (HTTP ${response.status})`);
      }
      if (refused.length) track('telemetry-rejected', { message: `The API permanently refused ${refused.length} event(s): ${refused.join(', ')}`.slice(0, 2000) });
    } catch { /* Persistent events remain available after reconnect/reload. */ }
    finally { running = false; timer ??= setTimeout(flushEvents, 30_000); }
  })();
}

export function track(type: string, fields: Omit<ClientEvent, 'type' | 'at'> = {}) {
  if (typeof window === 'undefined') return;
  try {
    updateBrowserErrorContext(fields);
    const event = normalizeEvent({ id: uuid(), at: new Date().toISOString(), sessionId, buildId: CLIENT_BUILD.buildId, type, ...fields,
      detail: { ...fields.detail, hotGeneration, storage: outbox.durable ? 'persistent' : 'memory-only', dropped: outbox.dropped } });
    void outbox.put(event).then(() => { timer ??= setTimeout(flushEvents, 2000); }).catch(() => undefined);
    const names: Record<string, string> = { 'overview-opened': 'open_share', 'debug-opened': 'open_debug', 'phone-opened': 'open_phone' };
    const action = names[type] ?? type.replaceAll('-', '_');
    if (ACTIONS.includes(action as typeof ACTIONS[number])) captureBrowserObservation('haere_user_action', { eventId: event.id, action, tripId: fields.tripId, planId: fields.planId,
      setting: fields.detail?.setting, value: fields.detail?.value });
  } catch { /* Observation must never break its caller. */ }
}

/** Local and cloud paths are independent; existing arbitrary detail is never forwarded. */
export const observe: Observe = (event, fields = {}) => {
  const observation = { ...fields, eventId: uuid() };
  try { track('observation', { tripId: typeof fields.tripId === 'string' ? fields.tripId : undefined, detail: { event, ...observation } }); } catch { /* Local isolation. */ }
  try { captureBrowserObservation(event, observation); } catch { /* Cloud isolation. */ }
};

export function errorDetail(error: unknown): { message: string; detail: Record<string, unknown> } {
  if (error instanceof Error) return { message: `${error.name}: ${error.message}`, detail: { stack: error.stack?.slice(0, 4000) } };
  return { message: String(error), detail: {} };
}

export function installTelemetry() {
  installBrowserErrorTracking(sessionId, (kind) => track('cloud-budget-exhausted', { message: `The page's cloud ${kind} budget is exhausted until reload; local diagnostics continue.` }), (error, fields = {}) => {
    // Global/React/map handlers retain their richer local events too. Caught transport/editor
    // failures otherwise need their original local stack even if cloud loading is stalled/off.
    if (fields.kind === 'handled') track('handled-error', { ...errorDetail(error), tripId: typeof fields.tripId === 'string' ? fields.tripId : undefined,
      planId: typeof fields.planId === 'string' ? fields.planId : undefined, detail: { ...errorDetail(error).detail, ...fields } });
  });
  window.addEventListener('error', (event) => {
    reportBrowserError(event.error ?? new Error(event.message), { kind: 'uncaught', source: event.filename, line: event.lineno, column: event.colno });
    track('error', { message: event.message, detail: { source: event.filename, line: event.lineno, column: event.colno, stack: event.error instanceof Error ? event.error.stack?.slice(0, 4000) : undefined } });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportBrowserError(event.reason, { kind: 'unhandledrejection' });
    track('error', { ...errorDetail(event.reason), detail: { ...errorDetail(event.reason).detail, kind: 'unhandledrejection' } });
  });
  window.addEventListener('online', flushEvents);
  window.addEventListener('pagehide', flushEvents);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushEvents(); });
  track('session-start', { detail: { path: location.pathname, userAgent: navigator.userAgent, viewport: `${window.innerWidth}x${window.innerHeight}`, language: navigator.language, build: CLIENT_BUILD } });
  observe('haere_app_started');
  observe('haere_screen_viewed', { screen: 'planner' });
  flushEvents();
}

if (import.meta.hot) import.meta.hot.on('vite:beforeUpdate', (payload) => {
  hotGeneration = `mixed-after-${Date.now()}`;
  track('hot-update', { detail: { updates: payload.updates.map((update) => ({ path: update.path, timestamp: update.timestamp })), attribution: 'Page may contain closures from earlier module generations.' } });
});
